import type { UserManagerSettings } from '@nrx/auth/react';

/**
 * Provider portal OIDC configuration.
 * Values come from Vite env vars, injected at build time from SSM by the CI pipeline.
 * Only public values are here — the Google client secret never appears in Vite vars.
 *
 * OIDC authority note:
 * oidc-client-ts performs OIDC discovery at {authority}/.well-known/openid-configuration.
 * The Cognito Hosted UI domain (*.amazoncognito.com) does NOT serve this endpoint.
 * Only the Cognito issuer URL serves it:
 *   https://cognito-idp.<region>.amazonaws.com/<userPoolId>
 *
 * We therefore set `authority` to the issuer URL (built from VITE_COGNITO_PROVIDER_USER_POOL_ID)
 * so that oidc-client-ts discovers the correct authorization/token/userinfo/logout endpoints.
 * Those discovered endpoints DO point to the Hosted UI domain because Cognito's OIDC metadata
 * document returns *.amazoncognito.com URLs for authorization_endpoint etc.
 */

const hostedUiDomain = import.meta.env['VITE_COGNITO_PROVIDER_AUTHORITY'] as string | undefined;
const clientId = import.meta.env['VITE_COGNITO_PROVIDER_CLIENT_ID'] as string | undefined;
const userPoolId = import.meta.env['VITE_COGNITO_PROVIDER_USER_POOL_ID'] as string | undefined;
const baseUrl = import.meta.env['VITE_PORTAL_BASE_URL'] as string | undefined;

// Derive the AWS region from the Hosted UI domain (e.g. "…auth.us-east-2.amazoncognito.com").
// Falls back to us-east-2 if the domain is not set (local dev without .env.sandbox).
const region = hostedUiDomain
  ? (hostedUiDomain.match(/\.auth\.([a-z0-9-]+)\.amazoncognito\.com/)?.[1] ?? 'us-east-2')
  : 'us-east-2';

// OIDC issuer URL — serves /.well-known/openid-configuration
const authority = userPoolId ? `https://cognito-idp.${region}.amazonaws.com/${userPoolId}` : '';

export const providerPortalAuthConfig: UserManagerSettings = {
  authority,
  client_id: clientId ?? '',
  redirect_uri: `${baseUrl ?? ''}/auth/callback`,
  post_logout_redirect_uri: `${baseUrl ?? ''}/auth/logout`,
  scope: 'openid profile email',
  response_type: 'code',
  automaticSilentRenew: true,
  loadUserInfo: true,
  monitorSession: true,
  validateSubOnSilentRenew: true,
};
